import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Session } from "@/types";

function useSessionsClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

export function useRenameSession(): UseMutationResult<
  Session,
  Error,
  { agentId: string; sessionId: string; title: string }
> {
  const qc = useQueryClient();
  const getClient = useSessionsClient();
  return useMutation({
    mutationFn: ({ agentId, sessionId, title }) =>
      getClient().patch<Session>(`/agents/${agentId}/sessions/${sessionId}`, { title }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.subResource("agents", vars.agentId, "sessions"),
      });
    },
  });
}

export function useArchiveSession(): UseMutationResult<
  { ok: true },
  Error,
  { agentId: string; sessionId: string }
> {
  const qc = useQueryClient();
  const getClient = useSessionsClient();
  return useMutation({
    mutationFn: ({ agentId, sessionId }) =>
      getClient().delete<{ ok: true }>(`/agents/${agentId}/sessions/${sessionId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.subResource("agents", vars.agentId, "sessions"),
      });
    },
  });
}
